import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, type Variants } from "motion/react";
import { Capacitor } from "@capacitor/core";
import { Languages, Settings2, X } from "lucide-react";

import { getTranslations } from "@/i18n";
import {
  NativeSettings,
  isNativeSettingsAvailable,
} from "@/lib/native/native-settings";
import { triggerLightTabHaptic } from "@/lib/native/haptics";
import { useScreen0Language } from "./language-provider";

type LanguageSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type LanguageModalCopy = ReturnType<typeof getTranslations>["language"];

type PlatformLanguageCopy = Pick<
  LanguageModalCopy,
  | "subtitle"
  | "openSettingsTitle"
  | "openSettingsSubtitle"
  | "footerReady"
>;

function resolvePlatformLanguageCopy(
  copy: LanguageModalCopy,
  supportsPerAppLocale: boolean,
): PlatformLanguageCopy {
  if (Capacitor.getPlatform() !== "android") {
    return {
      subtitle: copy.subtitle,
      openSettingsTitle: copy.openSettingsTitle,
      openSettingsSubtitle: copy.openSettingsSubtitle,
      footerReady: copy.footerReady,
    };
  }

  if (supportsPerAppLocale) {
    return {
      subtitle: copy.subtitleAndroid,
      openSettingsTitle: copy.openSettingsTitleAndroid,
      openSettingsSubtitle: copy.openSettingsSubtitleAndroid,
      footerReady: copy.footerReadyAndroid,
    };
  }

  return {
    subtitle: copy.subtitleAndroidLegacy,
    openSettingsTitle: copy.openSettingsTitleAndroidLegacy,
    openSettingsSubtitle: copy.openSettingsSubtitleAndroidLegacy,
    footerReady: copy.footerReadyAndroidLegacy,
  };
}

const POPOVER_SPRING = { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.9 };

function getBackdropVariants(prefersReducedMotion: boolean): Variants {
  if (prefersReducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0 } },
      exit: { opacity: 0, transition: { duration: 0 } },
    };
  }
  return {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.18 } },
  };
}

function getModalVariants(prefersReducedMotion: boolean): Variants {
  if (prefersReducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0 } },
      exit: { opacity: 0, transition: { duration: 0 } },
    };
  }
  return {
    hidden: { opacity: 0, scale: 0.96, y: 12 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: POPOVER_SPRING,
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      y: 8,
      transition: { duration: 0.18, ease: [0.4, 0, 1, 1] },
    },
  };
}

export function LanguageSettingsModal({ isOpen, onClose }: LanguageSettingsModalProps) {
  const {
    languageCode,
    languageLabel,
    systemLanguageTag,
    isUnsupportedSystemLanguage,
    supportsPerAppLocale,
  } = useScreen0Language();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const [isOpeningSettings, setIsOpeningSettings] = useState(false);
  const copy = getTranslations(languageCode).language;
  const platformCopy = useMemo(
    () => resolvePlatformLanguageCopy(copy, supportsPerAppLocale),
    [copy, supportsPerAppLocale],
  );

  const backdropVariants = useMemo(
    () => getBackdropVariants(prefersReducedMotion),
    [prefersReducedMotion],
  );
  const modalVariants = useMemo(
    () => getModalVariants(prefersReducedMotion),
    [prefersReducedMotion],
  );

  useEffect(() => {
    if (!isOpen) {
      setIsOpeningSettings(false);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const closeModal = () => {
    triggerLightTabHaptic();
    onClose();
  };

  const handleOpenSettings = async () => {
    if (isOpeningSettings) return;
    triggerLightTabHaptic();

    if (!isNativeSettingsAvailable()) {
      window.alert(
        `${copy.openSettingsUnavailableTitle}\n\n${copy.openSettingsUnavailableDescription}`,
      );
      return;
    }

    setIsOpeningSettings(true);
    try {
      await NativeSettings.openAppSettings();
    } catch (error) {
      console.error("Unable to open app settings", error);
      window.alert(
        `${copy.openSettingsUnavailableTitle}\n\n${copy.openSettingsUnavailableDescription}`,
      );
    } finally {
      setIsOpeningSettings(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-[max(16px,env(safe-area-inset-top))_16px_max(16px,env(safe-area-inset-bottom))]"
          role="presentation"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={backdropVariants}
        >
          <button
            type="button"
            className="absolute inset-0 border-none bg-transparent"
            aria-label={copy.closeLabel}
            onClick={closeModal}
          />

          <motion.section
            className="liquid-glass-popover relative z-[1] flex max-h-[min(88svh,640px)] w-full max-w-[26rem] flex-col overflow-hidden rounded-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-settings-title"
            aria-describedby="language-settings-copy"
            variants={modalVariants}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-4 px-4 pt-4 pb-2">
              <div className="grid min-w-0 gap-1">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[oklch(64%_0.04_155)]">
                  <Languages size={13} strokeWidth={2.2} aria-hidden />
                  {copy.eyebrow}
                </span>
                <h2
                  id="language-settings-title"
                  className="text-lg font-semibold tracking-tight text-[oklch(92%_0.02_150)]"
                >
                  {copy.title}
                </h2>
                <p
                  id="language-settings-copy"
                  className="text-sm leading-6 text-[oklch(74%_0.025_155)]"
                >
                  {platformCopy.subtitle}
                </p>
              </div>

              <button
                type="button"
                className="icon-button shrink-0"
                style={{ minWidth: 36, minHeight: 36, border: "none" }}
                onClick={closeModal}
                aria-label={copy.closeLabel}
              >
                <X size={18} strokeWidth={2.4} />
              </button>
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-3 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="liquid-glass-panel px-4">
                <div className="tap-row flex items-center justify-between gap-4">
                  <span className="flex items-center gap-3 text-sm text-[oklch(88%_0.02_150)]">
                    <Languages size={18} aria-hidden />
                    {copy.activeLanguageLabel}
                  </span>
                  <span className="text-sm font-semibold text-[oklch(90%_0.02_150)]">
                    {languageLabel}
                  </span>
                </div>
                <div className="tap-row flex items-center justify-between gap-4">
                  <span className="text-sm text-[oklch(88%_0.02_150)]">
                    {copy.systemLanguageLabel}
                  </span>
                  <span className="text-sm text-[oklch(66%_0.02_155)]">
                    {systemLanguageTag ?? copy.unavailableSystemValue}
                  </span>
                </div>
              </div>

              {isUnsupportedSystemLanguage ? (
                <p className="liquid-glass-alert-warning px-4 py-3 text-sm leading-6 text-[oklch(82%_0.055_85)]">
                  {copy.fallbackNote}
                </p>
              ) : null}

              <div className="liquid-glass-panel px-0">
                <button
                  type="button"
                  className="tap-row flex w-full items-center justify-between gap-3 px-4 text-left disabled:cursor-wait disabled:opacity-60"
                  onClick={() => void handleOpenSettings()}
                  disabled={isOpeningSettings}
                >
                  <span className="grid min-w-0 gap-0.5">
                    <span className="text-sm font-semibold text-[oklch(90%_0.02_150)]">
                      {isOpeningSettings
                        ? copy.openingSettingsLabel
                        : platformCopy.openSettingsTitle}
                    </span>
                    <span className="text-xs leading-5 text-[oklch(66%_0.02_155)]">
                      {platformCopy.openSettingsSubtitle}
                    </span>
                  </span>
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
                    aria-hidden="true"
                  >
                    <Settings2 size={18} strokeWidth={2.3} />
                  </span>
                </button>
              </div>
            </div>

            <footer className="liquid-glass-sheet-footer px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
              <p className="text-center text-xs leading-5 text-[oklch(58%_0.02_155)]">
                {isOpeningSettings ? copy.footerOpening : platformCopy.footerReady}
              </p>
            </footer>
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
